// Single source of truth for the bank's public-facing chrome —
// display name, logo, primary color, support URL, legal name.
// Reads /api/v1/branding (public, no JWT) and exposes the values
// through a context so any component can render them without
// re-fetching.
//
// The primary_color is also applied as a `--brand-primary` CSS
// variable on document.documentElement so design tokens that bind
// `var(--brand-primary)` pick it up across the whole app. Logo
// URL drives the favicon link tag too.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiJson } from '@/api/client'

export interface Branding {
  display_name: string
  legal_name: string
  logo_url: string
  primary_color: string
  support_url: string
}

const FALLBACK: Branding = {
  display_name: 'Antifraud',
  legal_name: 'Antifraud Platform',
  logo_url: '',
  primary_color: '',
  support_url: '',
}

function fetchBranding() {
  return apiJson<Branding>('/branding')
}

const BrandingCtx = createContext<Branding>(FALLBACK)

export function BrandingProvider({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: ['branding'],
    queryFn: fetchBranding,
    // Branding rarely changes. Long stale time + no auto-refetch
    // keeps DB pressure low; the admin PATCH already invalidates
    // the server cache so the next remount picks it up.
    staleTime: 5 * 60 * 1000,
    retry: 1,
    // Treat a hard failure as "fall back, don't block the UI".
    placeholderData: FALLBACK,
  })

  const branding: Branding = useMemo(
    () => ({ ...FALLBACK, ...(q.data ?? {}) }),
    [q.data],
  )

  // Apply primary color as a CSS variable. Allows existing design
  // tokens like `color: hsl(var(--brand-primary, …))` to bind.
  // Empty / invalid values fall through to the design-token
  // default already in :root.
  useEffect(() => {
    const root = document.documentElement
    if (branding.primary_color) {
      root.style.setProperty('--brand-primary', branding.primary_color)
    } else {
      root.style.removeProperty('--brand-primary')
    }
  }, [branding.primary_color])

  // Favicon. Touch the existing <link rel="icon"> if present;
  // create one otherwise. Skipped when logo_url is empty.
  useEffect(() => {
    if (!branding.logo_url) return
    let link = document.querySelector<HTMLLinkElement>(
      "link[rel~='icon']",
    )
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = branding.logo_url
  }, [branding.logo_url])

  return <BrandingCtx.Provider value={branding}>{children}</BrandingCtx.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- the provider + hook + context are the same surface; splitting them would force every consumer through a barrel import
export function useBranding(): Branding {
  return useContext(BrandingCtx)
}
